/**
 * Where this client is mounted, and therefore where its server is.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * The HUD is served under a prefix, and that prefix was written down **four times**: the Go
 * route group, Vite's `base`, the account API's constant, and the socket URL. Four copies of
 * one fact, and nothing checked them against each other — the exact shape this project has
 * been bitten by before, and the reason a mirrored vocabulary gets a test that walks it.
 *
 * Deployment made the cost concrete. `grind.townzone.net` serves the game at the **root**, so
 * the prefix is `/` there and `/grind/` in the dev stack behind Caddy. With four copies that
 * is four edits and three ways to be half-right: assets that 404 after the page loads, an API
 * that answers 404 to a login, a socket that never opens.
 *
 * ── The single fact ──────────────────────────────────────────────────────────
 *
 * `import.meta.env.BASE_URL` is Vite's own record of where the app is mounted — it is the same
 * value it writes into every generated asset URL, so it cannot disagree with them. Everything
 * else is derived from it here. Vite guarantees a leading and trailing slash, so `/grind/` and
 * `/` are both well-formed and neither needs special-casing.
 *
 * The server's half is `PUBLIC_BASE`, and the two are set from one place at build and deploy
 * time — see `scripts/deploy.sh` and `doc/grind/architecture/ROUTES-PORTS-ENV.md`.
 */

/**
 * The mount point, with a trailing slash. `/grind/` in development, `/` in production.
 *
 * Read through a function rather than captured at module load so a test can drive it, and so
 * nothing depends on import order.
 */
export function mountBase(): string {
  const base = import.meta.env.BASE_URL;
  // Defensive rather than decorative: `BASE_URL` is `/` under `vitest`, which does not run
  // Vite's config, and a missing trailing slash would silently concatenate into `/grindapi`.
  if (!base) return '/';
  return base.endsWith('/') ? base : `${base}/`;
}

/**
 * Where the account API lives.
 *
 * No trailing slash: every caller appends `/login`, `/characters` and so on, and two slashes
 * in a path is a request the router will not match.
 */
export function apiBase(base: string = mountBase()): string {
  return `${base}api`;
}

/**
 * The absolute WebSocket URL for the game connection.
 *
 * **Scheme follows the page**, so a site served over HTTPS gets `wss:` — a browser refuses a
 * plain `ws:` from a secure page, and that refusal is the entire failure mode of forgetting
 * it. `location.host` carries the port, which the dev stack needs and production does not.
 */
export function socketURL(
  characterID?: string,
  loc: { protocol: string; host: string } = location,
  base: string = mountBase(),
): string {
  const scheme = loc.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${scheme}//${loc.host}${base}ws`;
  return characterID ? `${url}?character=${encodeURIComponent(characterID)}` : url;
}
